// ============================================================================
// «Datos leídos por IA» (#1631): la lectura de la versión vigente (IA o lector
// CFDI) con su confianza, el tipo detectado y los campos; el usuario elige qué
// aplicar al documento (`reference`, `issued_at`, `expires_at`) y confirma con
// «Aplicar» (`PUT …/confirm-extracted { apply }`). La lectura corre en segundo
// plano al subir (≤ 60 s): mientras llega se muestra «Leyendo…» con «Actualizar»;
// si no llega, «Leer con IA». Los motivos por los que no corrió (presupuesto,
// IA no configurada, Sandbox, archivo grande…) son avisos, no errores. Si el
// backend aún no expone la lectura (404), se muestra «próximamente» sin romper.
// ============================================================================
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Button, Spinner, cn, type Tone } from "../../components/ui";
import { documentTypeName } from "../../utils/documentTypes";
import { APPLICABLE_EXTRACTED, type ApplicableExtracted, type ExtractedData, type ExtractionReason, type ShipmentDocument } from "../../services/shipmentDocuments";
import { DOCS_NS, shortDate, shortDateTime } from "./meta";

interface ExtractedPanelProps {
  document: ShipmentDocument;
  /** `null` = el backend respondió 404 (función próximamente). */
  available: boolean | null;
  /** La lectura encolada al subir sigue en curso (se está releyendo el documento). */
  pending: boolean;
  running: boolean;
  applying: boolean;
  /** Último motivo informativo del API (`already_extracted`, `budget_exceeded`…); se muestra como aviso. */
  notice: ExtractionReason | null;
  onRun: (force: boolean) => void;
  onRefresh: () => void;
  onApply: (apply: ApplicableExtracted[]) => void;
}

const CONFIDENCE_TONE: Record<string, Tone> = { high: "success", medium: "warning", low: "error" };
/** Motivos con texto propio; cualquier otro se muestra como «La lectura no corrió». */
const KNOWN_REASONS = new Set(["already_extracted", "duplicate", "budget_exceeded", "ai_not_configured", "sandbox", "too_large", "unsupported_type", "xml_not_cfdi", "empty", "ai_error", "version_stale", "too_many_requests"]);

export function ExtractedPanel({ document, available, pending, running, applying, notice, onRun, onRefresh, onApply }: ExtractedPanelProps) {
  const { t, i18n } = useTranslation(DOCS_NS);
  const extracted: ExtractedData | null = document.extracted;
  const stale = Boolean(extracted && extracted.version !== null && extracted.version !== document.current_version);
  const applicable = useMemo(() => (extracted?.fields ?? []).filter((f) => f.applicable && !(extracted?.applied ?? []).includes(f.key)), [extracted]);
  const [selected, setSelected] = useState<Set<string>>(() => new Set(applicable.map((f) => f.key)));
  useEffect(() => setSelected(new Set(applicable.map((f) => f.key))), [applicable]);

  const confirmed = Boolean(extracted?.confirmed);
  const isDate = (key: string) => key === "issued_at" || key === "expires_at";
  const valueText = (key: string, v: string | number | null) => (v === null ? "—" : isDate(key) && typeof v === "string" ? shortDate(v, i18n.language) : String(v));

  return (
    <section className="rounded-xl border border-line bg-card p-3" aria-labelledby="extracted-title" data-testid="extracted-panel">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h4 id="extracted-title" className="flex items-center gap-2 text-sm font-semibold text-content">
          <i className="ri-sparkling-2-line text-primary-500" /> {t("checklist.extracted.title")}
        </h4>
        <div className="flex items-center gap-1.5">
          {available === false || available === null ? (
            <Badge size="sm" tone="neutral">
              {t("common.comingSoon")}
            </Badge>
          ) : null}
          {extracted?.confidence ? (
            <Badge size="sm" tone={CONFIDENCE_TONE[extracted.confidence] ?? "neutral"}>
              {t(`checklist.extracted.confidence.${extracted.confidence}`)}
            </Badge>
          ) : null}
          {confirmed ? (
            <Badge size="sm" tone="success" dot>
              {t("checklist.extracted.confirmed")}
            </Badge>
          ) : null}
        </div>
      </div>

      {notice ? (
        <p role="status" className="mb-2 rounded-lg border border-warning/40 bg-warning/5 px-3 py-2 text-xs text-content" data-testid="extracted-notice">
          <i className="ri-information-line text-warning" /> {t(`checklist.extracted.reason.${KNOWN_REASONS.has(notice) ? notice : "other"}`)}
        </p>
      ) : null}

      {available === null || available === false ? (
        <p className="text-xs text-content-muted">{t("checklist.extracted.comingSoonText")}</p>
      ) : !document.has_file ? (
        <p className="text-xs text-content-muted">{t("checklist.extracted.needsFile")}</p>
      ) : pending && !extracted ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="flex items-center gap-2 text-xs text-content-muted">
            <Spinner size="sm" /> {t("checklist.extracted.queued")}
          </p>
          <Button size="sm" variant="ghost" icon={<i className="ri-refresh-line" />} onClick={onRefresh}>
            {t("checklist.extracted.refresh")}
          </Button>
        </div>
      ) : !extracted || stale ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-content-muted">{stale ? t("checklist.extracted.staleReading", { v: extracted?.version ?? 0 }) : t("checklist.extracted.none")}</p>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" icon={<i className="ri-refresh-line" />} onClick={onRefresh} disabled={running}>
              {t("checklist.extracted.refresh")}
            </Button>
            <Button size="sm" variant="secondary" loading={running} icon={<i className="ri-sparkling-2-line" />} onClick={() => onRun(false)}>
              {t("checklist.extracted.run")}
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {extracted.suggested_doc_type && extracted.suggested_doc_type !== document.doc_type ? (
            <p className="rounded-lg border border-info/40 bg-info/5 px-3 py-2 text-xs text-content">
              <i className="ri-lightbulb-line text-info" /> {t("checklist.extracted.suggested", { type: documentTypeName(extracted.suggested_doc_type, i18n.language) })}
            </p>
          ) : null}
          <ul className="divide-y divide-line rounded-lg border border-line">
            {extracted.fields.map((f) => {
              const canApply = f.applicable && !confirmed && !extracted.applied.includes(f.key);
              const applied = extracted.applied.includes(f.key);
              // `id` saneado: sólo [a-z0-9_-] (el id del documento y la llave vienen del API).
              const id = `extracted-${`${document.id}-${f.key}`.replace(/[^a-zA-Z0-9_-]/g, "_")}`;
              return (
                <li key={f.key} className="grid grid-cols-[auto_1fr_1.4fr] items-center gap-3 px-3 py-2">
                  <span className="flex w-5 justify-center">
                    {canApply ? (
                      <input
                        id={id}
                        type="checkbox"
                        checked={selected.has(f.key)}
                        disabled={applying}
                        onChange={(e) =>
                          setSelected((s) => {
                            const next = new Set(s);
                            if (e.target.checked) next.add(f.key);
                            else next.delete(f.key);
                            return next;
                          })
                        }
                        aria-label={t("checklist.extracted.applyField", { field: t(`checklist.extracted.field.${f.key}`, { defaultValue: f.key }) })}
                        className="h-4 w-4 accent-primary-500"
                      />
                    ) : applied ? (
                      <i className="ri-check-double-line text-success" title={t("checklist.extracted.confirmed")} aria-label={t("checklist.extracted.confirmed")} />
                    ) : null}
                  </span>
                  <label htmlFor={canApply ? id : undefined} className="text-xs font-medium text-content-secondary">
                    {t(`checklist.extracted.field.${f.key}`, { defaultValue: f.key })}
                  </label>
                  <span className={cn("truncate text-sm text-content", f.value === null && "text-content-faint")} title={f.value === null ? undefined : String(f.value)}>
                    {valueText(f.key, f.value)}
                  </span>
                </li>
              );
            })}
          </ul>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-[11px] text-content-muted">
              {extracted.at ? `${t("checklist.extracted.readAt")} ${shortDateTime(extracted.at, i18n.language)}` : null}
              {extracted.model ? ` · ${extracted.model}` : null}
              {extracted.version !== null ? ` · v${extracted.version}` : null}
            </span>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" loading={running} icon={<i className="ri-refresh-line" />} onClick={() => onRun(true)} disabled={applying}>
                {t("checklist.extracted.rerun")}
              </Button>
              {!confirmed ? (
                <Button size="sm" loading={applying} icon={<i className="ri-check-double-line" />} onClick={() => onApply(APPLICABLE_EXTRACTED.filter((k) => selected.has(k)))}>
                  {selected.size ? t("checklist.extracted.applyN", { n: selected.size }) : t("checklist.extracted.confirmOnly")}
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default ExtractedPanel;
